import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type { ComponentIdentifier, RegisterComponent } from './types';

export type RegisterOptions = {
	replaceExisting?: boolean;
};

const getComponentKey = ({ type, key }: ComponentIdentifier): string => `${type}:${key}`;

/**
 * Create a registry for UI control components (toolbar buttons, menu items, etc.).
 *
 * Follows the same pattern as `createBlockMenuRegistry` from `editor-plugin-block-menu`
 * but uses generic types to support any surface (toolbars, menus, etc.).
 *
 * Each component should have a unique `type` + `key` combination. The parent-child
 * model relies on key-based lookup.
 *
 * @returns A registry object with a `register` method and a `components` array.
 *
 * @example
 * ```ts
 * const registry = createRegistry();
 *
 * registry.register([
 *   {
 *     type: 'toolbar',
 *     key: 'primary-toolbar',
 *     component: ({ children }) => <div>{children}</div>,
 *   },
 *   {
 *     type: 'section',
 *     key: 'section-1',
 *     parents: [{ type: 'toolbar', key: 'primary-toolbar', rank: 1 }],
 *     component: ({ children }) => <div>{children}</div>,
 *   },
 * ]);
 * ```
 */
export const createRegistry = (): {
	components: RegisterComponent[];
	getComponent: (component: ComponentIdentifier) => RegisterComponent | undefined;
	register: (newComponents: RegisterComponent[], options?: RegisterOptions) => void;
	unregister: (components: ComponentIdentifier[]) => void;
} => {
	const components: RegisterComponent[] = [];
	const registeredComponents = new Map<string, RegisterComponent>();

	const register = (
		newComponents: RegisterComponent[],
		{ replaceExisting = false }: RegisterOptions = {},
	): void => {
		if (!isExperimentEnabled('platform_editor_slash_command')) {
			components.push(...newComponents);
			return;
		}

		const componentsToRegister = newComponents.filter((component) => {
			const componentKey = getComponentKey(component);
			if (registeredComponents.has(componentKey)) {
				if (replaceExisting) {
					registeredComponents.set(componentKey, component);
					const index = components.findIndex(
						(existing) => getComponentKey(existing) === componentKey,
					);
					if (index !== -1) {
						components[index] = component;
					}
				}
				return false;
			}

			registeredComponents.set(componentKey, component);
			return true;
		});

		components.push(...componentsToRegister);
	};

	return {
		getComponent: (component) => registeredComponents.get(getComponentKey(component)),
		register,
		unregister: (componentsToRemove) => {
			componentsToRemove.forEach((component) => {
				const componentKey = getComponentKey(component);
				registeredComponents.delete(componentKey);
				const index = components.findIndex(
					(existing) => getComponentKey(existing) === componentKey,
				);
				if (index !== -1) {
					components.splice(index, 1);
				}
			});
		},
		components,
	};
};
